/* =========================================================================
   Free + True - quick add on product cards

   Adds a single unit of a single-variant product to the cart without leaving
   the page, then refreshes and opens the theme's existing cart drawer.

   Three things this file deliberately does NOT do:

   1. It never reads, writes or displays a price. The wholesale price on a
      card is resolved by the B2B app inside the price element, which this
      code does not touch and the button does not duplicate. A quick-add that
      showed a figure would be showing the retail one.
   2. It never guesses a variant. Cards for multi-variant products render a
      link to the product page instead of a button, decided in Liquid.
   3. It binds one listener on `document`, never on the buttons, so cards that
      are swapped in by collection filtering or cloned by the homepage
      carousel keep working with no re-binding.

   The price posted to /cart/add.js is always Shopify's own - the endpoint
   accepts an id and a quantity and nothing else - which is exactly what the
   product page's add-to-cart form posts today. The B2B app adjusts the cart
   and checkout from there.
   ========================================================================= */

(function () {
  'use strict';

  if (window.ftQuickAddLoaded) return;
  window.ftQuickAddLoaded = true;

  var ADD_URL = '/cart/add.js';
  var RESET_AFTER = 2400;

  /* ---------- small DOM helpers (no closest(), the theme still boots on old
     browsers and a four line walk is cheaper than a polyfill) ---------- */

  function ancestorWithAttr(node, attr) {
    while (node && node !== document) {
      if (node.nodeType === 1 && node.getAttribute && node.getAttribute(attr) !== null) return node;
      node = node.parentNode;
    }
    return null;
  }

  function ancestorWithClass(node, cls) {
    var needle = ' ' + cls + ' ';
    while (node && node !== document) {
      if (node.nodeType === 1 && (' ' + (node.className || '') + ' ').indexOf(needle) > -1) return node;
      node = node.parentNode;
    }
    return null;
  }

  function label(btn, text) {
    var el = btn.querySelector('[data-ft-qa-label]');
    (el || btn).textContent = text;
  }

  function say(btn, message, isError) {
    var wrap = ancestorWithClass(btn, 'ft-qa');
    var el = wrap && wrap.querySelector('[data-ft-qa-status]');
    if (!el) return;
    el.textContent = message || '';
    el.className = 'ft-qa__status'
      + (message ? ' ft-qa__status--filled' : '')
      + (isError ? ' ft-qa__status--error' : '');
  }

  function attr(btn, name, fallback) {
    var v = btn.getAttribute(name);
    return (v === null || v === '') ? fallback : v;
  }

  /* ---------- POST to the cart ---------- */

  function addToCart(variantId, onDone, onFail) {
    var body = 'id=' + encodeURIComponent(variantId) + '&quantity=1';

    if (window.fetch) {
      window.fetch(ADD_URL, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: body
      }).then(function (response) {
        return response.json().then(
          function (data) { return { ok: response.ok, data: data }; },
          function () { return { ok: response.ok, data: null }; }
        );
      }).then(function (result) {
        if (result.ok) onDone(result.data); else onFail(result.data);
      })['catch'](function () { onFail(null); });
      return;
    }

    var xhr = new XMLHttpRequest();
    xhr.open('POST', ADD_URL, true);
    xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
    xhr.onload = function () {
      var data = null;
      try { data = JSON.parse(xhr.responseText); } catch (e) {}
      if (xhr.status >= 200 && xhr.status < 300) onDone(data); else onFail(data);
    };
    xhr.onerror = function () { onFail(null); };
    xhr.send(body);
  }

  /* ---------- hand back to the theme ----------
     ajaxCart.load() re-reads /cart.js, rebuilds the drawer contents from the
     theme's own Handlebars template and updates every .CartCount. That
     template already carries the B2B app's cart attributes, so drawer prices
     stay under the app's control exactly as they are after a product-page
     add. */

  function refreshCart() {
    try {
      if (window.ajaxCart && typeof window.ajaxCart.load === 'function') {
        window.ajaxCart.load();
        return true;
      }
    } catch (e) {}
    return false;
  }

  /* Opening the drawer, most specific route first. The cart toggle is an
     <a href="/cart">, so it is only clicked if a Javascript handler is
     genuinely bound to it - otherwise clicking it would navigate away. If
     neither route is available nothing happens: the item is still in the
     cart, the count has updated and the status line has said so. */

  function openDrawer() {
    try {
      if (window.timber && window.timber.RightDrawer && typeof window.timber.RightDrawer.open === 'function') {
        window.timber.RightDrawer.open();
        return true;
      }
    } catch (e) {}

    try {
      var $ = window.jQuery;
      if (!$ || typeof $._data !== 'function') return false;
      var toggles = document.querySelectorAll('.js-drawer-open-right');
      for (var i = 0; i < toggles.length; i++) {
        var events = $._data(toggles[i], 'events');
        if (events && events.click && events.click.length) {
          $(toggles[i]).trigger('click');
          return true;
        }
      }
    } catch (e) {}

    return false;
  }

  /* ---------- one delegated listener ---------- */

  function onClick(event) {
    var btn = ancestorWithAttr(event.target, 'data-ft-qa-add');
    if (!btn) return;

    event.preventDefault();

    if (btn.getAttribute('aria-busy') === 'true') return;

    var variantId = btn.getAttribute('data-ft-qa-variant');
    if (!variantId) return;

    var restLabel = attr(btn, 'data-ft-qa-label-rest', 'Add to bag');
    var busyLabel = attr(btn, 'data-ft-qa-label-busy', 'Adding…');
    var doneLabel = attr(btn, 'data-ft-qa-label-done', 'Added');
    var doneMsg = attr(btn, 'data-ft-qa-msg-done', 'Added to your bag.');
    var failMsg = attr(btn, 'data-ft-qa-msg-fail', 'That could not be added just now. Open the product page to try again.');

    btn.setAttribute('aria-busy', 'true');
    btn.disabled = true;
    label(btn, busyLabel);
    say(btn, '');

    addToCart(variantId, function () {
      label(btn, doneLabel);
      say(btn, doneMsg, false);
      refreshCart();
      openDrawer();

      window.setTimeout(function () {
        btn.removeAttribute('aria-busy');
        btn.disabled = false;
        label(btn, restLabel);
      }, RESET_AFTER);

    }, function (data) {
      /* Shopify answers 422 with a human sentence for the common cases -
         sold out, or already holding every unit in stock. Prefer it. */
      var message = (data && (data.description || data.message)) || failMsg;
      btn.removeAttribute('aria-busy');
      btn.disabled = false;
      label(btn, restLabel);
      say(btn, message, true);
    });
  }

  document.addEventListener('click', onClick, false);
})();
